import type { TFunction } from 'i18next'
import {
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agion/ui'
import { ChevronDown } from 'lucide-react'
import { diskSizeBounds, emptyDiskKeepsCurrent } from './vmDeployment'
import { AzureDiskHints, GcpDiskHints, UpcloudDiskHints } from './diskHints'

/**
 * Advanced VM settings — progressive-disclosure block used by the onboarding
 * wizard and the re-apply dialog. Provider-aware so the zone semantics stay
 * correct: GCP zones look like `europe-north1-b`, Azure Availability Zones
 * are `1`/`2`/`3`, and UpCloud uses the environment region directly.
 * GCP and Azure placement controls are single-select in Track A; Azure stores
 * a 0-or-1 element list so Track C can extend to spread without an interface
 * rename.
 *
 * Empty-string zone/diskSizeGb mean "use module default" (an UpCloud
 * re-apply keeps the current disk instead); DFS toggle is
 * always explicit because its state directly gates cloud-storage resource
 * creation (GCS bucket on GCP, storage account + private endpoint on Azure,
 * Managed Object Storage on UpCloud).
 *
 * Rehydration callers (ApplyTerraformDialog) must use strict boolean coercion
 * `raw === true || raw === 'true'` — `Boolean('false') === true` in JS would
 * silently flip the wrong way when env.Config stores the string "false".
 */

const DFS_REPLICATION_OPTIONS = [
  { value: 'LRS', label: 'LRS (Locally redundant, single zone)' },
  { value: 'ZRS', label: 'ZRS (Zone-redundant, within region)' },
  { value: 'GRS', label: 'GRS (Geo-redundant, paired region)' },
  { value: 'RAGRS', label: 'RAGRS (Read-access geo-redundant)' },
  { value: 'GZRS', label: 'GZRS (Geo-zone redundant)' },
  { value: 'RAGZRS', label: 'RAGZRS (Read-access geo-zone redundant)' },
]

// Sentinel for the "Automatic (regional)" Select option. Radix Select
// reserves "" for clearing, so empty-list state can't be a SelectItem
// value — we use a real non-empty sentinel and map it to [] in the
// callback. Never sent to the backend; purely a UI discriminator.
const AZ_AUTO_SENTINEL = '__auto'
const GCP_ZONE_SUFFIXES = Array.from({ length: 3 }, (_, index) => String.fromCharCode(97 + index))
const SSH_CIDR_PLACEHOLDER = String.fromCharCode(42)

interface CommonProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  region: string
  diskSizeGb: string
  onDiskSizeChange: (size: string) => void
  dfsEnabled: boolean
  onDfsEnabledChange: (enabled: boolean) => void
  t: TFunction<readonly ['pages']>
}

interface GcpProps extends CommonProps {
  provider: 'gcp'
  zone: string
  onZoneChange: (zone: string) => void
  /** Re-apply: empty keeps the current disk, and a new size rebuilds the VM. */
  reapply?: boolean
  /** Size the last successful apply recorded, when known. */
  appliedDiskGb?: number
}

interface AzureProps extends CommonProps {
  provider: 'azure'
  hideAccess?: boolean
  /** Re-apply: empty resends the recorded disk, and a new size deallocates the VM. */
  reapply?: boolean
  /** Size the last successful apply recorded, when known. */
  appliedDiskGb?: number
  // Azure stores a 0-or-1 element list in Track A so Track C can extend it
  // to spread without a breaking interface rename. UI renders a single-select.
  availabilityZones: string[]
  onAvailabilityZonesChange: (zones: string[]) => void
  dfsReplication: string
  onDfsReplicationChange: (r: string) => void
  diagnosticsEnabled: boolean
  onDiagnosticsEnabledChange: (b: boolean) => void
  logRetentionDays: string
  onLogRetentionDaysChange: (s: string) => void
  // Comma-separated in the UI; caller normalizes to list on submit.
  // Empty input normalizes to ["*"] to match module default.
  sshAllowedCidrs: string
  onSshAllowedCidrsChange: (s: string) => void
}

interface UpcloudProps extends CommonProps {
  provider: 'upcloud'
  /** Storage the selected plan includes; undefined for a plan typed by hand. */
  includedStorageGb?: number
  /** Re-apply: empty keeps the current disk, and UpCloud disks only grow. */
  reapply?: boolean
  /** Size the last successful apply recorded, when known. */
  appliedDiskGb?: number
}

export type AdvancedSettingsProps = GcpProps | AzureProps | UpcloudProps

function clampDiskSize(raw: string, min: number, max: number): string {
  const trimmed = raw.trim()
  if (trimmed === '') return ''
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return ''
  return String(Math.min(max, Math.max(min, Math.round(n))))
}

function reapplyDisk(props: AdvancedSettingsProps): { reapply: boolean; appliedDiskGb?: number } {
  return { reapply: !!props.reapply, appliedDiskGb: props.appliedDiskGb }
}

function clampLogRetention(raw: string): string {
  const trimmed = raw.trim()
  if (trimmed === '') return ''
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return ''
  return String(Math.min(730, Math.max(30, Math.round(n))))
}

export function AdvancedSettings(props: AdvancedSettingsProps) {
  const { t, open, onOpenChange } = props
  const bounds = diskSizeBounds(props.provider)
  const { reapply, appliedDiskGb } = reapplyDisk(props)
  // UpCloud disks cannot shrink, so a re-apply raises the floor to the
  // current disk. GCP accepts a smaller size and rebuilds around it, so its
  // floor stays at the image size and the warning carries the consequence.
  const diskMin = props.provider === 'upcloud' && reapply
    ? Math.max(bounds.min, appliedDiskGb ?? 0)
    : bounds.min

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          />
          {t('pages:onboarding.configure.advanced', 'Advanced settings')}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 pt-3">
        {/* Zone / Availability Zone — semantically equivalent: placement / capacity override */}
        {props.provider === 'gcp' && (
          <div className="space-y-2">
            <Label htmlFor="adv-zone">
              {t('pages:onboarding.configure.zone', 'Zone')}
            </Label>
            <Select value={props.zone} onValueChange={props.onZoneChange}>
              <SelectTrigger id="adv-zone" className="max-w-xs">
                <SelectValue
                  placeholder={t(
                    'pages:onboarding.configure.zoneDefault',
                    { defaultValue: 'Default ({{region}}-b)', region: props.region },
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {GCP_ZONE_SUFFIXES.map(letter => (
                  <SelectItem key={letter} value={`${props.region}-${letter}`}>
                    {props.region}-{letter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t(
                'pages:onboarding.configure.zoneHint',
                'Pick a specific zone if the default zone is out of capacity.',
              )}
            </p>
          </div>
        )}
        {props.provider === 'azure' && (
          <div className="space-y-2">
            <Label htmlFor="adv-az">
              {t('pages:onboarding.configure.availabilityZone', 'Availability Zone')}
            </Label>
            <Select
              // Controlled value: empty state maps to the Automatic
              // sentinel so the Select always shows a concrete selection
              // and the user can revert a prior pin back to automatic.
              value={props.availabilityZones[0] ?? AZ_AUTO_SENTINEL}
              onValueChange={v => props.onAvailabilityZonesChange(
                v === AZ_AUTO_SENTINEL ? [] : [v],
              )}
            >
              <SelectTrigger id="adv-az" className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AZ_AUTO_SENTINEL}>
                  {t(
                    'pages:onboarding.configure.availabilityZoneDefault',
                    'Automatic (regional)',
                  )}
                </SelectItem>
                {['1', '2', '3'].map(z => (
                  <SelectItem key={z} value={z}>
                    {t('pages:onboarding.configure.availabilityZoneLabel', {
                      defaultValue: 'Zone {{zone}}',
                      zone: z,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t(
                'pages:onboarding.configure.availabilityZoneHint',
                'Pin the VM to a specific Availability Zone. Automatic lets Azure place it regionally.',
              )}
            </p>
          </div>
        )}

        {/* Boot disk size — shared */}
        <div className="space-y-2">
          <Label htmlFor="adv-disk-size">
            {t('pages:onboarding.configure.diskSize', 'Boot disk size (GB)')}
          </Label>
          <Input
            id="adv-disk-size"
            type="number"
            min={diskMin}
            max={bounds.max}
            value={props.diskSizeGb}
            onChange={e => props.onDiskSizeChange(e.target.value)}
            onBlur={e => props.onDiskSizeChange(clampDiskSize(e.target.value, diskMin, bounds.max))}
            placeholder={emptyDiskKeepsCurrent(props.provider, reapply, appliedDiskGb)
              ? t('pages:onboarding.configure.diskCurrentPlaceholder', 'Current size')
              : '50'}
            className="max-w-xs"
          />
          {props.provider === 'upcloud' ? (
            <UpcloudDiskHints
              t={t}
              diskSizeGb={props.diskSizeGb}
              reapply={reapply}
              appliedDiskGb={appliedDiskGb}
              includedStorageGb={props.includedStorageGb}
            />
          ) : props.provider === 'gcp' ? (
            <GcpDiskHints t={t} diskSizeGb={props.diskSizeGb} reapply={reapply} appliedDiskGb={appliedDiskGb} />
          ) : (
            <AzureDiskHints t={t} diskSizeGb={props.diskSizeGb} reapply={reapply} appliedDiskGb={appliedDiskGb} />
          )}
        </div>

        {/* DFS enabled — shared */}
        <div className="flex items-start gap-2">
          <Checkbox
            id="adv-dfs-enabled"
            checked={props.dfsEnabled}
            onCheckedChange={v => props.onDfsEnabledChange(v === true)}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="adv-dfs-enabled" className="cursor-pointer">
              {t(
                'pages:onboarding.configure.dfsEnabled',
                'Provision durable file storage',
              )}
            </Label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {props.provider === 'gcp' && t(
                'pages:onboarding.configure.dfsHintGcp',
                'GCS bucket for the agent-host durable file store. Enabled by default on new environments.',
              )}
              {props.provider === 'azure' && t(
                'pages:onboarding.configure.dfsHintAzure',
                'Azure Blob storage with private endpoint for the agent-host durable file store. Enabled by default on new environments.',
              )}
              {props.provider === 'upcloud' && t(
                'pages:onboarding.configure.dfsHintUpcloud',
                'UpCloud Managed Object Storage for the agent-host durable file store.',
              )}
            </p>
          </div>
        </div>

        {/* Azure-only extras */}
        {props.provider === 'azure' && (
          <>
            {/* DFS replication — only relevant when DFS is on, but kept
                visible (not disabled) so operators can pre-select before
                enabling. Azure ignores the value when DFS is off. */}
            <div className="space-y-2">
              <Label htmlFor="adv-dfs-replication">
                {t('pages:onboarding.configure.dfsReplication', 'DFS storage replication')}
              </Label>
              <Select value={props.dfsReplication} onValueChange={props.onDfsReplicationChange}>
                <SelectTrigger id="adv-dfs-replication" className="max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DFS_REPLICATION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t(
                  'pages:onboarding.configure.dfsReplicationHint',
                  'LRS is the cheapest and default; ZRS/GRS add durability at higher cost. Change only when compliance or DR requires it.',
                )}
              </p>
            </div>

            {/* Diagnostics toggle */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="adv-diagnostics"
                checked={props.diagnosticsEnabled}
                onCheckedChange={v => props.onDiagnosticsEnabledChange(v === true)}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label htmlFor="adv-diagnostics" className="cursor-pointer">
                  {t(
                    'pages:onboarding.configure.diagnosticsEnabled',
                    'Provision Log Analytics workspace and Azure Monitor Agent',
                  )}
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t(
                    'pages:onboarding.configure.diagnosticsHint',
                    'Enabled by default. Disable only when an Azure Policy forbids per-deployment Log Analytics workspaces.',
                  )}
                </p>
              </div>
            </div>

            {/* Log retention days */}
            <div className="space-y-2">
              <Label htmlFor="adv-log-retention">
                {t('pages:onboarding.configure.logRetentionDays', 'Log retention (days)')}
              </Label>
              <Input
                id="adv-log-retention"
                type="number"
                min={30}
                max={730}
                value={props.logRetentionDays}
                onChange={e => props.onLogRetentionDaysChange(e.target.value)}
                onBlur={e => props.onLogRetentionDaysChange(clampLogRetention(e.target.value))}
                placeholder="30"
                className="max-w-xs"
                disabled={!props.diagnosticsEnabled}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t(
                  'pages:onboarding.configure.logRetentionHint',
                  '30..730 days for the PerGB2018 SKU. Ignored when diagnostics are disabled.',
                )}
              </p>
            </div>

            {/* SSH allowed CIDRs — comma-separated text input */}
            {!props.hideAccess && <div className="space-y-2">
              <Label htmlFor="adv-ssh-cidrs">
                {t('pages:onboarding.configure.sshAllowedCidrs', 'SSH allowed sources')}
              </Label>
              <Input
                id="adv-ssh-cidrs"
                type="text"
                value={props.sshAllowedCidrs}
                onChange={e => props.onSshAllowedCidrsChange(e.target.value)}
                placeholder={SSH_CIDR_PLACEHOLDER}
                className="max-w-md"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t(
                  'pages:onboarding.configure.sshAllowedCidrsHint',
                  'Comma-separated CIDRs. Use * for any (default). Narrow to operator ranges for hardened deployments.',
                )}
              </p>
            </div>}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
